<template>
  <div
    v-if="showUpdatePrompt"
    class="fixed bottom-4 left-4 right-4 md:left-auto md:right-4 md:w-96 z-50 pwa-update-prompt"
  >
    <div class="bg-white dark:bg-gray-800 rounded-2xl shadow-lg border border-gray-200 dark:border-gray-700 p-4">
      <div class="flex items-start">
        <div class="flex-shrink-0">
          <div class="w-12 h-12 bg-blue-100 dark:bg-blue-900 rounded-lg flex items-center justify-center">
            <svg class="w-6 h-6 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
          </div>
        </div>
        <div class="ml-3 flex-1">
          <h3 class="text-sm font-semibold text-gray-900 dark:text-white">
            {{ __('New Version Available') }}
          </h3>
          <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">
            {{ __('A new version of the app is ready. To get the latest features and improvements, update the app.') }}
          </p>
          <div class="flex mt-3 space-x-2">
            <button
              @click="updateApp"
              :disabled="isUpdating"
              class="flex-1 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 disabled:cursor-not-allowed text-white text-sm font-medium py-2 px-4 rounded-md transition-colors"
            >
              <span v-if="!isUpdating">{{ __('Update') }}</span>
              <span v-else class="flex items-center justify-center">
                <svg class="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                  <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                  <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                {{ __('Updating...') }}
              </span>
            </button>
            <button
              @click="dismissUpdate"
              :disabled="isUpdating"
              class="w-[100px] bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed text-gray-700 dark:text-gray-300 text-sm font-medium py-2 px-4 rounded-md transition-colors"
            >
              {{ __('Later') }}
            </button>
          </div>
        </div>
        <button
          @click="dismissUpdate"
          :disabled="isUpdating"
          class="flex-shrink-0 ml-2 text-gray-400 hover:text-gray-500 dark:hover:text-gray-300 disabled:opacity-50"
        >
          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'PWAUpdatePrompt',
  data() {
    return {
      showUpdatePrompt: false,
      isUpdating: false,
      registration: null,
      updateAvailable: false,
      shouldReload: false, // Flag to control reload
      controllerChangeHandler: null,
    }
  },
  mounted() {
    // Check for service worker updates
    if ('serviceWorker' in navigator) {
      // Create controller change handler (but don't attach it yet)
      this.controllerChangeHandler = () => {
        if (this.shouldReload) {
          // Only reload if user clicked update button
          window.location.reload()
        }
      }

      // Wait a bit before checking for updates to ensure service worker is fully installed
      // This prevents showing update prompt on first install
      setTimeout(() => {
        this.checkForUpdates()
      }, 2000)

      // Check for updates every 30 minutes (reduced frequency to prevent server overload)
      setInterval(() => {
        this.checkForUpdates()
      }, 30 * 60 * 1000)

      // Listen for messages from service worker
      // IMPORTANT: Only show update prompt if there's already an active service worker
      // Check registration.active to distinguish between first install and update
      navigator.serviceWorker.addEventListener('message', async (event) => {
        if (event.data && event.data.type === 'SW_UPDATED') {
          // Wait a bit to ensure service worker state is stable
          await new Promise(resolve => setTimeout(resolve, 500))
          
          // Check if there's an active service worker (not first install)
          const registration = await navigator.serviceWorker.getRegistration()
          
          // CRITICAL: Check if there's a waiting service worker
          // If there's no waiting worker, this might be first install activation
          // Only show update prompt if there's both an active AND a waiting service worker
          if (registration && registration.active !== null && registration.waiting !== null) {
            // Only show update prompt if there's an active service worker AND a waiting one (real update)
            console.log('[PWA Update] SW_UPDATED message received (update detected - active and waiting workers exist)')
            this.updateAvailable = true
            this.showUpdatePrompt = true
          } else {
            console.log('[PWA Update] SW_UPDATED message received but no waiting worker (first install or no update, ignoring)', {
              hasActive: registration && registration.active !== null,
              hasWaiting: registration && registration.waiting !== null
            })
          }
        }
      })
    }
  },
  beforeUnmount() {
    // Remove event listener if it was attached
    if (this.controllerChangeHandler && 'serviceWorker' in navigator) {
      navigator.serviceWorker.removeEventListener('controllerchange', this.controllerChangeHandler)
    }
  },
  methods: {
    async checkForUpdates() {
      if (!('serviceWorker' in navigator)) {
        return
      }

      try {
        const registration = await navigator.serviceWorker.getRegistration()
        if (!registration) {
          return
        }

        this.registration = registration

        // Check for updates
        await registration.update()

        // CRITICAL: Check if there's an active service worker BEFORE checking for updates
        // If registration.active exists, it means a service worker was previously installed
        // This is the key to distinguishing between first install and update
        const hasActiveServiceWorker = registration.active !== null

        // Check if there's a waiting service worker
        // IMPORTANT: Only show update prompt if there's already an active service worker
        // If active is null, this is the first installation, not an update
        if (registration.waiting && hasActiveServiceWorker) {
          console.log('[PWA Update] Waiting service worker detected (update available)')
          this.updateAvailable = true
          this.showUpdatePrompt = true
          return
        }

        // Also check for installing service worker
        // IMPORTANT: Only show update prompt if there's already an active service worker
        // If active is null, this is the first installation, not an update
        if (registration.installing && hasActiveServiceWorker) {
          console.log('[PWA Update] Installing service worker detected (update), waiting for it to finish...')
          // Store reference to installing worker
          const installingWorker = registration.installing
          // Wait for it to finish installing
          installingWorker.addEventListener('statechange', () => {
            // Check if worker still exists and has the expected state
            // Only show prompt if there's an active service worker (not first install)
            if (installingWorker && installingWorker.state === 'installed' && hasActiveServiceWorker) {
              console.log('[PWA Update] Service worker installed and waiting (update)')
              this.updateAvailable = true
              this.showUpdatePrompt = true
            }
          })
        }

        // Listen for installing service worker (only if not already listening)
        // IMPORTANT: Only listen for updates if there's already an active service worker
        // If active is null, this is the first installation, not an update
        if (!registration._updateFoundListener && hasActiveServiceWorker) {
          registration._updateFoundListener = true
          registration.addEventListener('updatefound', () => {
            const newWorker = registration.installing

            if (!newWorker) {
              return
            }

            // Only show prompt once when service worker is installed
            // Only show if there's an active service worker (not first install)
            const stateChangeHandler = () => {
              // Check if worker still exists before accessing state
              // CRITICAL: Only show update prompt if active service worker exists (not first install)
              // Re-check hasActiveServiceWorker inside handler to ensure it's still valid
              const stillHasActive = registration.active !== null
              if (newWorker && newWorker.state === 'installed' && stillHasActive) {
                // There's a new service worker available (this is an update, not first install)
                console.log('[PWA Update] New service worker installed (update detected)')
                if (!this.showUpdatePrompt) {
                  this.updateAvailable = true
                  this.showUpdatePrompt = true
                }
                // Remove listener after use
                if (newWorker) {
                  newWorker.removeEventListener('statechange', stateChangeHandler)
                }
              }
            }

            newWorker.addEventListener('statechange', stateChangeHandler)
          })
        }
      } catch (error) {
        console.error('Error checking for updates:', error)
      }
    },
    async updateApp() {
      console.log('[PWA Update] Update button clicked')
      
      // Get registration if not available
      if (!this.registration) {
        this.registration = await navigator.serviceWorker.getRegistration()
      }

      if (!this.registration) {
        console.error('[PWA Update] No service worker registration found')
        this.showUpdatePrompt = false
        return
      }

      // Check for waiting service worker
      if (!this.registration.waiting) {
        console.log('[PWA Update] No waiting service worker found, checking for updates...')
        
        // Force update check
        await this.registration.update()
        
        // Wait a bit for update to complete and check again
        await new Promise(resolve => setTimeout(resolve, 1500))
        
        // Re-check registration after update
        this.registration = await navigator.serviceWorker.getRegistration()
        
        if (!this.registration || !this.registration.waiting) {
          console.log('[PWA Update] No update available after check')
          if (this.registration) {
            console.log('[PWA Update] Registration state:', {
              waiting: this.registration.waiting ? this.registration.waiting.state : null,
              installing: this.registration.installing ? this.registration.installing.state : null,
              active: this.registration.active ? this.registration.active.state : null,
            })
          }
          this.showUpdatePrompt = false
          return
        }
      }

      console.log('[PWA Update] Waiting service worker found, proceeding with update')

      this.isUpdating = true
      this.shouldReload = true // Set flag to allow reload

      try {
        // Attach controller change handler now (only when user wants to update)
        if (this.controllerChangeHandler && 'serviceWorker' in navigator) {
          navigator.serviceWorker.addEventListener('controllerchange', this.controllerChangeHandler)
        }

        // Tell the waiting service worker to skip waiting and become active
        console.log('[PWA Update] Sending SKIP_WAITING message to service worker')
        this.registration.waiting.postMessage({ type: 'SKIP_WAITING' })

        // Wait for controller change with a timeout
        let controllerChanged = false
        const originalController = navigator.serviceWorker.controller
        
        // Set up controller change listener
        const controllerChangeHandler = () => {
          controllerChanged = true
          console.log('[PWA Update] Controller changed, new service worker is active')
        }
        
        navigator.serviceWorker.addEventListener('controllerchange', controllerChangeHandler)

        // Also poll for controller change (backup method)
        const checkInterval = setInterval(() => {
          const currentController = navigator.serviceWorker.controller
          if (currentController !== originalController) {
            controllerChanged = true
            clearInterval(checkInterval)
            navigator.serviceWorker.removeEventListener('controllerchange', controllerChangeHandler)
            console.log('[PWA Update] Controller changed (polling), new service worker is active')
          }
        }, 100)
        
        // Wait for controller change or timeout after 3 seconds
        await new Promise((resolve) => {
          if (controllerChanged) {
            clearInterval(checkInterval)
            navigator.serviceWorker.removeEventListener('controllerchange', controllerChangeHandler)
            resolve()
            return
          }
          
          // Timeout after 3 seconds - reload anyway
          setTimeout(() => {
            clearInterval(checkInterval)
            navigator.serviceWorker.removeEventListener('controllerchange', controllerChangeHandler)
            if (!controllerChanged) {
              console.log('[PWA Update] Timeout waiting for controller change, reloading anyway')
            }
            resolve()
          }, 3000)
        })

        // Small delay to ensure everything is ready
        await new Promise(resolve => setTimeout(resolve, 300))

        // Reload the page to get the new version
        console.log('[PWA Update] Reloading page to apply update')
        window.location.reload()
      } catch (error) {
        console.error('[PWA Update] Error updating app:', error)
        this.isUpdating = false
        this.shouldReload = false
        // Remove event listener on error
        if (this.controllerChangeHandler && 'serviceWorker' in navigator) {
          navigator.serviceWorker.removeEventListener('controllerchange', this.controllerChangeHandler)
        }
        // Show error to user (optional)
        alert('خطا در آپدیت اپلیکیشن. لطفاً صفحه را refresh کنید.')
      }
    },
    dismissUpdate() {
      this.showUpdatePrompt = false
      // Show again after 1 hour
      setTimeout(() => {
        if (this.updateAvailable) {
          this.showUpdatePrompt = true
        }
      }, 60 * 60 * 1000)
    },
  },
}
</script>

<style scoped>
.pwa-update-prompt {
  animation: slideUp 0.3s ease-out;
}

@keyframes slideUp {
  from {
    transform: translateY(100%);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
}
</style>

